import styled from "styled-components";

import {
	Text,
	Button,
	ColorV2,
	InputPrice,
	Tabs,
} from "@adoptaunabuelo/react-components";
import ResumeStyled from "../../../components/container/ResumeContainer";
import { useEffect, useState } from "react";
import { State } from "../../../redux";
import { useSelector } from "react-redux";
import ReliefModal from "../../../components/modal/ReliefModal";
import media from "styled-media-query";

const Header = styled.div`
	padding: 24px 24px 16px;
	text-align: center;
	${media.lessThan("medium")`
		padding: 0;
		text-align: left;
    `}
	
`;
const DataView = styled.div<{ $isMobile?: boolean }>`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: ${(props) => (props.$isMobile ? "24px 24px 112px" : "24px")};
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const InputView = styled.div<{ $isMobile?: boolean }>`
	display: flex;
	flex-direction: column;
	flex: 1;
	justify-content: ${(props) => (props.$isMobile ? "flex-start" : "center")};
`;
const TabsView = styled.div<{ $isMobile?: boolean }>`
	padding: 0 60px;
	margin-bottom: ${(props) => (props.$isMobile ? "40px" : "24px")};

	${(props) =>
		props.$isMobile
			? `
		@media (min-height: 700px) {
			margin-bottom: 96px;
		}

		@media (min-height: 820px) {
			margin-bottom: 132px;
		}
    `
			: ""}
`;
const PriceSelectorView = styled.div<{ $isMobile?: boolean }>`
	> div > div:first-child > * {
		max-width: 60% !important;
	}

	${(props) =>
		props.$isMobile
			? `
		> div > div:first-child > * {
			max-width: 60% !important;
		}

		> div {
			gap: 12px;
		}

		> div > div:nth-child(2) {
			gap: 10px;
		}

		> div > div:nth-child(2) > div,
		> div > div:nth-child(3) {
			border-radius: 24px;
			padding: 18px 16px;
		}
    `
			: ""}
`;

const Resume = (props: Props) => {
	const appLocation = useSelector((state: State) => state.location.location);
	const isMobile = useSelector((state: State) => state.screen.isMobile);

	const [priceOptions, setPriceOptions] = useState<{ price: number }[]>([{ price: 9 }, { price: 15 }, { price: 20 }]);
	const [defaultPrice, setDefaultPrice] = useState<number>(0);
	const [conversion, setConversion] = useState<number>(1);
	const [selectedPrice, setSelectedPrice] = useState(15);
	const [selectedCharge, setSelectedCharge] = useState("monthly");
	const [reliefAmount, setReliefAmount] = useState("0");
	const [showReliefModal, setShowReliefModal] = useState(false);
	const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);

	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		const temp1 = getOptionsPrice(selectedCharge);
		setPriceOptions(temp1);
		const temp2 = getDefaultPrice(selectedCharge);
		setDefaultPrice(temp2);
		setSelectedPrice(temp2);
		const temp3 = getConversionPrice();
		setConversion(temp3);
		props.onPriceChange &&
			props.onPriceChange(selectedPrice, selectedCharge);
	}, [selectedCharge, appLocation]);

	useEffect(() => {
		getReliefAmount(selectedPrice);
		props.onPriceChange &&
			props.onPriceChange(selectedPrice, selectedCharge);
	}, [selectedPrice, appLocation]);

	const getReliefAmount = (price: number) => {
		const reliefSection1 = price > 250 ? 250 : price;
		const reliefSection2 = price > 250 ? price - 250 : 0;
		const relief = reliefSection1 * 0.8 + reliefSection2 * 0.4;
		setReliefAmount(relief.toFixed(2));
	};

	const getDefaultPrice = (charge: string) => {
		if (charge === "monthly") {
			return appLocation
				? appLocation.currency.price.monthly.default
				: 15;
		} else {
			return appLocation
				? appLocation.currency.price.annually.default
				: 150;
		}
	};

	const getOptionsPrice = (charge: string) => {
		if (charge === "monthly") {
			return appLocation &&
				appLocation.currency.price.monthly.inputOptions
				? appLocation.currency.price.monthly.inputOptions.map((i) => ({
					price: i,
				}))
				: [{ price: 9 }, { price: 15 }, { price: 20 }];
		} else {
			return appLocation &&
				appLocation.currency.price.annually.inputOptions
				? appLocation.currency.price.annually.inputOptions.map((i) => ({
					price: i,
				}))
				: [{ price: 90 }, { price: 150 }, { price: 250 }];
		}
	};

	const getConversionPrice = () => {
		return appLocation ? appLocation.currency.conversion : 1;
	};

	const reliefAmountParts = reliefAmount.split(".");

	const handleCustomPriceInput = (
		event: React.FormEvent<HTMLDivElement>
	) => {
		const target = event.target;
		if (!(target instanceof HTMLInputElement)) return;
		if (target.placeholder !== "Otra cantidad") return;

		const price = Number(target.value);
		if (!Number.isFinite(price) || price <= 0) return;

		setSelectedPrice(price);
	};

	const mobileResumeStyle: React.CSSProperties = isMobile
		? {
			minHeight:
				"calc(var(--doc-height) - var(--nav-bar-height-mobile))",
			borderRadius: "24px 24px 0 0",
		}
		: {};
	const inputPriceKey = `${appLocation?.currency.name || "eur"}-${selectedCharge}-${priceOptions.map((option) => option.price).join("-")}-${defaultPrice}`;
	const tabsKey = `${isMobile ? "mobile" : "desktop"}-${windowWidth}`;

	return (
		<ResumeStyled
			style={{
				padding: 0,
				overflow: "hidden",
				maxWidth: "100vw",
				marginBottom: 0,
				zIndex: 1,
				...mobileResumeStyle,
				...props.style,
			}}
			childrenProps={{
				style: { flex: 1 },
			}}
		>
			<ReliefModal
				isVisible={showReliefModal}
				price={selectedPrice}
				charge={selectedCharge}
				onClose={() => setShowReliefModal(false)}
			/>
			<Header>
				{!isMobile && (
					<Text
						type="h4"
						weight="semibold"
						style={{ textAlign: "center" }}
					>
						Apoya a las personas mayores siendo embajador
					</Text>
				)}
			</Header>

			<DataView $isMobile={isMobile}>
				{isMobile && (
					<Text
						type="h3"
						weight="semibold"
						style={{ marginBottom: 24, textAlign: "center" }}
					>
						Apoya a las personas mayores siendo embajador
					</Text>
				)}
				<InputView
					$isMobile={isMobile}
					onInput={handleCustomPriceInput}
				>
					<TabsView $isMobile={isMobile}>
						<Tabs
							key={tabsKey}
							style={{ width: "100%" }}
							design={"third"}
							textColor={ColorV2.text.neutralHard}
							cellColor={ColorV2.surface.neutralSoft}
							options={[
								{
									id: "monthly",
									title: "Mensual",
								},
								{
									id: "annually",
									title: "Anual",
								},
							]}
							onChange={(op) => {
								setSelectedCharge(op.id);
							}}
						/>
					</TabsView>
					<PriceSelectorView $isMobile={isMobile}>
						<InputPrice
							key={inputPriceKey}
							style={{ marginBottom: 32 }}
							currency={
								appLocation ? appLocation.currency.symbol : "€"
							}
							options={priceOptions}
							defaultOption={defaultPrice}
							label={
								isMobile
									? "Acompañaremos a un mayor durante {{value}} horas"
									: "Acompañaremos a un mayor durante {{value}} horas"
							}
							labelValueConversion={0.8 * conversion}
							hideCustomAmount={true}
							onChange={(price) => setSelectedPrice(price)}
						/>
					</PriceSelectorView>
				</InputView>
				{appLocation &&
					appLocation.country_code === "ES" &&
					appLocation.currency.name === "eur" && (
						<div style={{ marginBottom: isMobile ? 0 : 24 }}>
							<Text
								type="c2"
								style={{
									textAlign: "center",
								}}
							>
								Te devolverán{" "}
								<span
									style={{
										backgroundColor:
											ColorV2.surface.secondary,
										padding: "1px 6px",
										borderRadius: 3,
										fontWeight: 500,
									}}
								>
									{reliefAmountParts[0]}
									<span>
										{reliefAmountParts[1] !== "00" &&
											"," + reliefAmountParts[1]}
										{" €"}
									</span>
								</span>{" "}
								en la Declaración de la Renta
							</Text>
							<Text
								type="c2"
								style={{
									textAlign: "center",
									marginTop: 4,
									textDecoration: "underline",
									cursor: "pointer",
								}}
								onClick={() => setShowReliefModal(true)}
							>
								Saber más
							</Text>
						</div>
					)}
				<Col
					style={{
						gap: 8,
						display: isMobile ? "none" : "flex",
					}}
				>
					<Button loading={props.loading} onClick={props.onClick}>
						Quiero donar{" "}
						{selectedPrice.toLocaleString("es-ES", {
							useGrouping: true,
						})}{" "}
						{appLocation ? appLocation.currency.symbol : "€"}{" "}
						{selectedCharge === "monthly"
							? "al mes"
							: selectedCharge === "annually"
								? "al año"
								: ""}
					</Button>
				</Col>
			</DataView>
		</ResumeStyled>
	);
};
export default Resume;
export interface Props {
	style?: React.CSSProperties;
	loading: boolean;
	onClick: () => void;
	onPriceChange?: (price: number, charge: string) => void;
	onShareClick: (op: { id: string }) => void;
}
